import { useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import useVersionCheck from '../../hooks/useVersionCheck'

/*
 * Picks up a new deploy without ever interrupting the user.
 *
 * When a newer build is detected we do nothing until the user navigates to a different
 * screen — at that point they are between tasks, there is no half-filled form to lose,
 * and a full page load is indistinguishable from a slightly slow route change.
 *
 * Must be rendered inside <Router>; it renders nothing.
 */

const RELOADED_KEY = 'versionReloadedAt'

export default function VersionReloader() {
    const updateAvailable = useVersionCheck()
    const { pathname } = useLocation()
    const history = useHistory()
    const initialPathname = useRef(pathname)

    useEffect(() => {
        if (!updateAvailable) return
        // The effect also fires on mount and whenever `updateAvailable` flips; neither
        // is a navigation.
        if (pathname === initialPathname.current) return
        // Redirects (`/` -> `/dashboard`, the `*` catch-all, permission bounces) are the
        // app moving the user, not the user choosing to leave a screen.
        if (history.action === 'REPLACE') return

        try {
            // A version.json that disagrees with the bundle — a partial deploy, a CDN
            // serving a mismatched pair — would otherwise reload on every click.
            if (window.sessionStorage.getItem(RELOADED_KEY)) return
            window.sessionStorage.setItem(RELOADED_KEY, String(Date.now()))
        } catch {
            // Without storage we cannot bound this to one reload, so don't start.
            return
        }

        // react-router has already pushed the target URL, so this lands on the screen
        // the user asked for, running the new build.
        window.location.reload()
    }, [pathname, updateAvailable, history.action])

    return null
}
